import currentAgentService from './currentAgentService';

export const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || 'http://localhost:8000/api';
export const MIN_BAIL_THRESHOLD = import.meta.env?.VITE_MIN_BAIL_THRESHOLD || 25000;

/**
 * ✅ FULLY DYNAMIC phone number functions - Only source of truth
 */
export const getDynamicPhoneNumber = async () => {
  try {
    const phoneFormatted = await currentAgentService?.getCurrentAgentPhoneFormatted();
    console.log('🔢 Dynamic phone (formatted):', phoneFormatted);
    return phoneFormatted;
  } catch (error) {
    console.error('❌ Failed to get dynamic phone number:', error);
    // Final fallback - use current agent service's fallback
    return await currentAgentService?.getCurrentAgentPhoneFormatted() || '+19162563468';
  }
};

export const getDynamicPhoneText = async () => {
  try {
    const phoneText = await currentAgentService?.getCurrentAgentPhoneText();
    console.log('📞 Dynamic phone text:', phoneText);
    return phoneText;
  } catch (error) {
    console.error('❌ Failed to get dynamic phone text:', error);
    return 'Call Duty Agent (916) 256-3468';
  }
};

export const getDynamicPhoneDisplay = async () => {
  try {
    const phoneDisplay = await currentAgentService?.getCurrentAgentPhone();
    console.log('📱 Dynamic phone display:', phoneDisplay);
    return phoneDisplay;
  } catch (error) {
    console.error('❌ Failed to get dynamic phone display:', error);
    return '(916) 256-3468';
  }
};

// Inmate search configuration
export const SEARCH_CONFIG = {
  maxResultsPerPage: 25,
  maxTotalResults: 1000,
  debounceDelay: 300,
  sessionLimit: 5
};

// Status configurations
export const CUSTODY_STATUS = {
  IN_CUSTODY: { color: 'red', label: 'In Custody' },
  RELEASED: { color: 'green', label: 'Released' },
  DISMISSED: { color: 'blue', label: 'Dismissed' },
  UNKNOWN: { color: 'gray', label: 'Unknown' }
};

// Enhanced Damian CTA and Pain-Point Messaging Configuration - COMPREHENSIVE STRATEGY IMPLEMENTATION
export const DAMIAN_MESSAGING = {
  // Call-to-Action types from strategy document
  CTAS: {
    START_BAIL: 'start_bail_process',
    CALL_DAMIAN: 'call_damian', 
    REQUEST_CALLBACK: 'request_callback',
    LEARN_OPTIONS: 'learn_options',
    PAYMENT_OPTIONS: 'payment_options',
    START_BAIL_NOW: 'start_bail_now', // Sticky prompt CTA
    EMERGENCY_CALL: 'emergency_call'
  },
  
  // Pain-point detection keywords - ENHANCED FROM STRATEGY
  PAIN_POINTS: {
    OVERWHELMED: ['overwhelmed', 'urgent', 'dont know', 'help', 'confused', 'scared', 'stressed', 'panic'],
    MONEY_CONCERNS: ['money', 'cant afford', 'cost', 'payment', 'expensive', 'broke', 'financial', 'budget'],
    LEAVE_THEM: ['lesson', 'teach', 'leave them', 'let them sit', 'consequences', 'accountability', 'responsibility'],
    SAFETY_CONCERNS: ['safety', 'stable', 'work', 'school', 'family', 'home', 'job', 'employment'],
    BOUNDARIES: ['boundaries', 'conditions', 'expectations', 'rules', 'guidelines', 'structure'],
    UNSURE: ['dont know', 'unsure', 'not sure', 'hesitate', 'maybe', 'thinking', 'doubt', 'uncertain'],
    NAIVE_REQUESTS: ['speeding ticket bail', 'free bail', 'without paying', 'no cost'],
    ILLEGAL_REQUESTS: ['hide from police', 'avoid arrest', 'evade law', 'fake documents']
  },
  
  // Decision logic thresholds
  THRESHOLDS: {
    ELIGIBLE_BAIL_THRESHOLD: MIN_BAIL_THRESHOLD,
    MIN_BAIL_AMOUNT: 1000,
    SEARCH_LIMIT: 5
  },
  
  // Response templates mapping to strategy sections - COMPREHENSIVE IMPLEMENTATION
  TEMPLATES: {
    // Pain Point Templates
    C1: 'overwhelmed_urgent',
    C2: 'parents_teach_lesson',
    C3: 'safety_stability', 
    C4: 'consequences_accountability',
    C5: 'money_worries',
    C6: 'not_sure_yet',
    C7: 'boundaries_safety_plan',
    
    // Objection Handling Templates
    D1: 'objection_jail_lesson',
    D2: 'objection_cant_afford',
    D3: 'objection_not_worth_helping',
    D4: 'objection_repeat_offender',
    
    // Educational Templates
    E1: 'education_what_bail_does',
    E2: 'education_pros_posting_bail',
    E3: 'education_cons_considerations',
    E4: 'education_payment_basics',
    
    // Conversion Tactics Templates
    F1: 'urgency_risk_waiting',
    F2: 'normalize_decision',
    F3: 'simplify_process',
    F4: 'humanize_support',
    F5: 'direct_action_close'
  },

  // Conversion Tactics - FROM STRATEGY DOCUMENT
  CONVERSION_TACTICS: {
    URGENCY: "Every extra night in custody can mean lost work, stronger criminal ties, or personal safety risks.",
    NORMALIZE: "Most families choose bail to stabilize things while court plays out.",
    SIMPLIFY: "It only takes 10–15 minutes to start paperwork. I can begin for you now.",
    HUMANIZE: "You don't have to do this alone — I'm here 24/7 to walk you through it.",
    DIRECT_ACTION: "Want me to connect you right now?"
  },

  // Pain Point Response Patterns - STRATEGY IMPLEMENTATION
  PAIN_POINT_RESPONSES: {
    PARENTS_TEACH_LESSON: {
      opening: "I hear you — wanting accountability makes sense.",
      concern: "A heads-up: jail isn't a structured program. Folks can pick up worse influences, connect with people who teach them how to dodge responsibility, or get exposed to drugs or threats.",
      solution: "If you want a middle ground, we can set firm release conditions and clear expectations while keeping them safe and showing up to court.",
      cta: "We can set boundaries while still getting them out safely."
    },
    
    MONEY_CONCERNS: {
      opening: "Money\'s real — I hear you.",
      acknowledgment: "I understand costs are a real concern. Let me explain what options we have...",
      solution: "Sometimes we can structure payments or discuss collateral (case-by-case). Want me to outline what's possible?",
      cta: "Offer callback or explain payment plans"
    },
    
    DOUBT_UNSURE: {
      opening: "It\'s natural to have questions. Here\'s exactly what happens next...",
      guidance: "I know this is stressful. Let me walk you through this step by step.",
      solution: "Outline pros/cons; highlight work, school, family impact.",
      cta: "Want me to connect you with our agent to talk it out?"
    },
    
    ACCOUNTABILITY_CONSEQUENCES: {
      opening: "Bail ≠ forgiveness. Courts still require compliance.",
      clarification: "Show bail as accountability + safety. Posting bail doesn't remove court accountability — it just means they face it outside jail.",
      cta: "Show bail as accountability + safety"
    }
  }
};

// Utility functions for external integration
export const getSessionId = () => {
  try {
    const key = 'damian_session_id';
    let sid = localStorage?.getItem(key);
    if (!sid) {
      sid = 'sess_' + Math.random()?.toString(36)?.slice(2) + Date.now()?.toString(36);
      localStorage?.setItem(key, sid);
    }
    return sid;
  } catch {
    return 'sess_fallback';
  }
};

export const formatCurrency = (amount) => {
  if (amount == null) return '—';
  const num = Number(amount);
  if (!Number.isFinite(num) || num <= 0) return '—';
  return num?.toLocaleString('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    maximumFractionDigits: 0 
  });
};

export const isEligibleForBail = (record, threshold = MIN_BAIL_THRESHOLD) => {
  return record?.status === 'IN_CUSTODY' && Number(record?.bail_amount || 0) >= threshold;
};

// Enhanced pain-point detection utility - COMPREHENSIVE STRATEGY
export const detectPainPoints = (message) => {
  const lowerMessage = message?.toLowerCase();
  const detected = [];
  
  Object.entries(DAMIAN_MESSAGING?.PAIN_POINTS)?.forEach(([painPoint, keywords]) => {
    if (keywords?.some(keyword => lowerMessage?.includes(keyword))) {
      detected?.push(painPoint?.toLowerCase());
    }
  });
  
  return detected;
};

// Enhanced intent classification - STRATEGY ALIGNED
export const classifyIntent = (message) => {
  const lowerMessage = message?.toLowerCase();
  
  // Emergency situations - HIGH PRIORITY
  if (lowerMessage?.includes('urgent') || lowerMessage?.includes('emergency') || lowerMessage?.includes('asap')) {
    return { type: 'EMERGENCY', priority: 'high' };
  }
  
  // Teaching lesson objection - HIGH PRIORITY FROM STRATEGY
  if (DAMIAN_MESSAGING?.PAIN_POINTS?.LEAVE_THEM?.some(keyword => lowerMessage?.includes(keyword))) {
    return { type: 'OBJECTION_LESSON', priority: 'high' };
  }
  
  // Money concerns - HIGH PRIORITY FROM STRATEGY
  if (DAMIAN_MESSAGING?.PAIN_POINTS?.MONEY_CONCERNS?.some(keyword => lowerMessage?.includes(keyword))) {
    return { type: 'PAYMENT_INQUIRY', priority: 'high' };
  }
  
  // Search requests
  if (['search', 'find', 'look for', 'arrestee', 'inmate']?.some(keyword => lowerMessage?.includes(keyword))) {
    return { type: 'SEARCH', priority: 'medium' };
  }
  
  // General bail information
  if (['bail', 'bond', 'process', 'how does', 'what is']?.some(keyword => lowerMessage?.includes(keyword))) {
    return { type: 'BAIL_INFO', priority: 'medium' };
  }
  
  // Facility information
  if (['jail', 'facility', 'where', 'location']?.some(keyword => lowerMessage?.includes(keyword))) {
    return { type: 'FACILITY_INFO', priority: 'low' };
  }
  
  // Greeting patterns
  if (['hello', 'hi', 'hey', 'good morning', 'good afternoon']?.some(keyword => lowerMessage?.includes(keyword))) {
    return { type: 'GREETING', priority: 'low' };
  }
  
  return { type: 'GENERAL_INQUIRY', priority: 'low' };
};